import React, { useEffect, useState } from 'react';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';

interface CustomTooltipProps {
  title: string | React.ReactNode;
  children: React.ReactElement;
  placement?: TooltipProps['placement'];
  arrow?: boolean;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  title,
  children,
  placement = 'top',
  arrow = false,
}) => {
  const [open, setOpen] = useState(false);
  const childRef = React.useRef<HTMLElement | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const isDarkMode =
    typeof document !== 'undefined' &&
    (
      document.documentElement.classList.contains('dark') ||
      document.body.classList.contains('dark') ||
      document.documentElement.getAttribute('data-theme') === 'dark' ||
      document.body.getAttribute('data-theme') === 'dark'
    );
  const tooltipBg = isDarkMode ? 'rgba(20, 20, 20, 0.92)' : 'rgba(255, 255, 255, 0.98)';
  const tooltipColor = isDarkMode ? '#f5f5f5' : '#111111';
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)';
  const boxShadow = '0 8px 24px rgba(0, 0, 0, 0.24)';

  useEffect(() => {
    const onScroll = () => setOpen(false);
    const dropdown = typeof document !== 'undefined' ? document.querySelector('[data-scroll-container="true"]') : null;    
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('select-menu-scroll', onScroll, { passive: true });    
    if (dropdown) dropdown.addEventListener('scroll', onScroll as EventListener, { passive: true } as any);
    
    const selectMenus = document.querySelectorAll('[class*="select__menu"]');
    selectMenus.forEach((menu) => {
      menu.addEventListener('scroll', onScroll, { passive: true });
    });
    
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('select-menu-scroll', onScroll);
      if (dropdown) dropdown.removeEventListener('scroll', onScroll as EventListener);
      selectMenus.forEach((menu) => {
        menu.removeEventListener('scroll', onScroll);
      });
    };
  }, []);

  useEffect(() => {
    const node = anchorEl;
    if (!node) return;

    const onScroll = () => setOpen(false);
    const subscribed: HTMLElement[] = [];

    let current: HTMLElement | null = node;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;
      const isScrollable =
        ['auto', 'scroll', 'overlay'].includes(overflowY) ||
        ['auto', 'scroll', 'overlay'].includes(overflowX);

      if (isScrollable) {
        current.addEventListener('scroll', onScroll, { passive: true });
        subscribed.push(current);
      }
      current = current.parentElement;
    }

    return () => {
      subscribed.forEach((el) => el.removeEventListener('scroll', onScroll));
    };
  }, [anchorEl]);

  const setCombinedRef = (node: HTMLElement | null) => {
    childRef.current = node;
    setAnchorEl(node);
    const { ref } = children as any;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref && typeof ref === 'object') {
      ref.current = node;
    }
  };

  const child = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      children.props?.onMouseEnter?.(e);
      setOpen(true);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      children.props?.onMouseLeave?.(e);
      setOpen(false);
    },
    ref: setCombinedRef,
  });

  if (!title || title === '') return children;

  return (
    <Tooltip 
      title={title}
      placement={placement}
      arrow={arrow}
      open={open}
      disableTouchListener
      disableFocusListener
      slotProps={{
        popper: {

          style: { zIndex: 9999 },
        },
        tooltip: {
          sx: {
            bgcolor: tooltipBg,
            color: tooltipColor,
            border: `1px solid ${borderColor}`,
            boxShadow,
            padding: '3px 5px',
            borderRadius: '6px',
            fontSize: '12px',
            margin: '2px',
            whiteSpace: 'pre-line',
          },
        },
        arrow: {
          sx: {
            color: tooltipBg,
          },
        },
      }}
    >
      {child}
    </Tooltip>
  );
};

export default CustomTooltip;